import { usePaginationAdmin } from '@diamond/shared/context';
import type { SelectOption } from '@diamond/shared/types';
import { useCleanSearchParams, useSearchbox } from '@diamond/sol-admin/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PLANT_OPTIONS } from '../constants';
import type { CollectionDateFilterFields, LHCRowData } from '../types';
import { buildLhcFilter } from '../utils/build-lhc-params';
import { getDateRangeByPeriod } from '../utils/date-filter-period';
import { useCollectionPlants, useLHCList } from './use-collection-data';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getCurrentPageFromUrl = (params: URLSearchParams): number => {
  return parseInt(params.get('page') || '1', 10);
};

const getPageSizeFromUrl = (params: URLSearchParams): number => {
  const pageSize = parseInt(
    params.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE;
};

type DraftLhcFilters = {
  plantValue: SelectOption | null;
  cabangValue: SelectOption | null;
};

type AppliedLhcFilters = DraftLhcFilters & {
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
};

const DEFAULT_DRAFT_FILTERS: DraftLhcFilters = {
  plantValue: null,
  cabangValue: null,
};

const DEFAULT_APPLIED_FILTERS: AppliedLhcFilters = {
  ...DEFAULT_DRAFT_FILTERS,
  dateFrom: '',
  dateTo: '',
  searchQuery: '',
};

const toAppliedLhcFilters = (input: {
  draftFilters: DraftLhcFilters;
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
}): AppliedLhcFilters => ({
  plantValue: input.draftFilters.plantValue,
  cabangValue: input.draftFilters.cabangValue,
  dateFrom: input.dateFrom,
  dateTo: input.dateTo,
  searchQuery: input.searchQuery.trim(),
});

export function useLhcListPage() {
  const navigate = useNavigate();
  const { searchParams: urlParams, setParams } = useCleanSearchParams();
  const currentPage = getCurrentPageFromUrl(urlParams);
  const pageSize = getPageSizeFromUrl(urlParams);
  const searchHook = useSearchbox({ isDebounce: false, resetPage: true });
  const [draftFilters, setDraftFilters] = useState<DraftLhcFilters>(
    DEFAULT_DRAFT_FILTERS
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedLhcFilters>(
    () => ({
      ...DEFAULT_APPLIED_FILTERS,
      searchQuery: searchHook.params.query.trim(),
    })
  );
  const methods = useForm<CollectionDateFilterFields>({
    defaultValues: {
      'lhc_date-from': '',
      'lhc_date-to': '',
    },
  });
  const { data: collectionPlantsData, refetch: refetchCollectionPlants } =
    useCollectionPlants();
  const plantOptions = useMemo(() => {
    if (collectionPlantsData?.data?.length) {
      return collectionPlantsData.data.map((plant) => ({
        label: plant.name,
        value: plant.id,
      }));
    }

    return PLANT_OPTIONS;
  }, [collectionPlantsData]);

  useEffect(() => {
    const normalizedQueryFromUrl = searchHook.params.query.trim();
    setAppliedFilters((prev) => {
      if (prev.searchQuery === normalizedQueryFromUrl) {
        return prev;
      }

      return { ...prev, searchQuery: normalizedQueryFromUrl };
    });
  }, [searchHook.params.query]);

  const apiParams = useMemo(
    () =>
      buildLhcFilter({
        page: currentPage,
        size: pageSize,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
        plantValue: appliedFilters.plantValue,
        cabangValue: appliedFilters.cabangValue,
        searchQuery: appliedFilters.searchQuery,
      }),
    [appliedFilters, currentPage, pageSize]
  );
  const { data: apiData, isLoading, refetch } = useLHCList(apiParams);

  const setCurrentPage = useCallback(
    (page: number) => {
      setParams({ page: page.toString() });
    },
    [setParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setParams({ pageSize: size.toString(), page: '1' });
    },
    [setParams]
  );

  const handleSearch = useCallback(() => {
    const nextAppliedFilters = toAppliedLhcFilters({
      draftFilters,
      dateFrom: methods.getValues('lhc_date-from'),
      dateTo: methods.getValues('lhc_date-to'),
      searchQuery: searchHook.getValues('search_query') || '',
    });

    setAppliedFilters(nextAppliedFilters);
    searchHook.handleSearch();
  }, [draftFilters, methods, searchHook]);

  const handleSearchChange = useCallback(
    (value: string) => {
      searchHook.setValue('search_query', value, { shouldDirty: true });
    },
    [searchHook]
  );

  const handleClearSearch = useCallback(() => {
    searchHook.setValue('search_query', '', { shouldDirty: true });
  }, [searchHook]);

  const handleDateFilter = useCallback((period: string) => {
    return getDateRangeByPeriod(period);
  }, []);

  const updateDateParams = useCallback(
    (fromDate: string, toDate: string) => {
      methods.setValue('lhc_date-from', fromDate);
      methods.setValue('lhc_date-to', toDate);
    },
    [methods]
  );

  const handlePlantChange = useCallback((newValue: SelectOption | null) => {
    setDraftFilters((prev) => ({ ...prev, plantValue: newValue }));
  }, []);

  const handleCabangChange = useCallback((newValue: SelectOption | null) => {
    setDraftFilters((prev) => ({ ...prev, cabangValue: newValue }));
  }, []);

  const totalPages = useMemo(() => {
    return apiData?.pagination?.pages ?? 0;
  }, [apiData]);

  const validCurrentPage = useMemo(() => {
    if (currentPage < 1) return 1;
    if (currentPage > totalPages && totalPages > 0) return totalPages;
    return currentPage;
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (validCurrentPage !== currentPage) {
      setCurrentPage(validCurrentPage);
    }
  }, [currentPage, setCurrentPage, validCurrentPage]);

  const tableData = useMemo<LHCRowData[]>(() => {
    return apiData?.data ?? [];
  }, [apiData]);

  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    validCurrentPage,
    setCurrentPage,
    totalPages
  );

  const searchValue = searchHook.watch('search_query') || '';

  const navigateToDetail = useCallback(
    (id: string) => {
      navigate(`/collection-management/${id}`);
    },
    [navigate]
  );

  const navigateHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return {
    apiParams,
    currentPage,
    draftFilters,
    firstPage,
    handleCabangChange,
    handleClearSearch,
    handleDateFilter,
    handlePlantChange,
    handleSearch,
    handleSearchChange,
    isLoading,
    lastPage,
    methods,
    navigateHome,
    navigateToDetail,
    nextPage,
    pageSize,
    plantOptions,
    previousPage,
    refetch,
    refetchCollectionPlants,
    searchValue,
    setPageSize,
    tableData,
    totalPages,
    updateDateParams,
    validCurrentPage,
  };
}
